import React, { useEffect, useMemo, useState } from "react";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import CardPatientRecordInterfaceUX from "react-lib/apps/HISV3/CLM/CardPatientRecordInterfaceUX";
import { adToBe, dateToStringBE, formatUTCtoBEString } from "react-lib/utils/dateUtils";
import { Checkbox, Icon } from "semantic-ui-react";
import formatComma from "react-lib/utils/formatComma";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import { State, WidthsType, GenerateTableHeader } from "./sequence/PatientRecordInterface";

// Types
type CardPatientRecordInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: any;
  runSequence: any;
  // Sequence
  PatientRecordInterfaceSequence: State["PatientRecordInterfaceSequence"];
  masterOptions?: Record<string, any>;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  languageUX?: any;
};

// Const
const CARD_PATIENT_RECORD_INTERFACE = "CardPatientRecordInterface";

const CardPatientRecordInterface = (props: CardPatientRecordInterfaceProps) => {
  const [selectedItem, setSelectedItem] = useState<any[]>([]);
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    props.runSequence({
      sequence: "PatientRecordInterface",
      restart: true,
    });
  }, []);

  useEffect(() => {
    if (
      props.errorMessage?.[CARD_PATIENT_RECORD_INTERFACE] ||
      props.successMessage?.[CARD_PATIENT_RECORD_INTERFACE]
    ) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const columnTable = (data: any, style?: any) => {
    return <div style={{ ...style, marginLeft: "10px" }}>{data}</div>;
  };

  const columnTableIconCheck = (data: any, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : data === false ? (
          <Icon name="times circle" color="red" size="big" />
        ) : (
          ""
        )}
      </div>
    );
  };

  const formatBirthdate = (date: any) => {
    if (date.length !== 8) return "";
    const day = date.slice(0, 2);
    const month = date.slice(2, 4);
    const year = date.slice(4, 8);

    let birthdate = `${day}/${month}/${year}`;

    return adToBe(birthdate);
  };

  const handleSelectAll = (e: any, v: any) => {
    if (v.checked) {
      let newData = (props.PatientRecordInterfaceSequence?.patientRecordList || []).map(
        (item: any) => item.id
      );

      setSelectedItem(newData);
    } else {
      setSelectedItem([]);
    }
  };

  const handleSearchData = () => {
    props.runSequence({
      sequence: "PatientRecordInterface",
      action: "search",
      cardKey: CARD_PATIENT_RECORD_INTERFACE,
      page: 0,
    });

    setSelectedItem([]);
  };

  const handleClearFilter = () => {
    props.runSequence({
      sequence: "PatientRecordInterface",
      action: "clear",
    });

    setSelectedItem([]);
  };

  const handleSaveData = (type: string) => {
    props.runSequence({
      sequence: "PatientRecordInterface",
      action: "save",
      cardKey: CARD_PATIENT_RECORD_INTERFACE,
      type,
      selectedItem,
    });

    setSelectedItem([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    props.setProp(`PatientRecordInterfaceSequence.searchPatientRecord.${data.name}`, data?.value);
  };

  const handleChangeDate = (date: any) => {
    props.setProp(`PatientRecordInterfaceSequence.searchPatientRecord.createDate`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`PatientRecordInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "PatientRecordInterface",
      action: "search",
      cardKey: CARD_PATIENT_RECORD_INTERFACE,
      [key]: value,
    });

    setSelectedItem([]);
  };

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedItem.length === 0) return true;
  }, [selectedItem]);

  const patientRecordData = useMemo(() => {
    return (props.PatientRecordInterfaceSequence?.patientRecordList || []).map((item: any) => {
      const createdName = (props.PatientRecordInterfaceSequence?.userOptions || []).find(
        (data: any) => data?.id === item.created_by
      );

      return {
        ...item,
        check: columnTableCenter(
          <Checkbox
            checked={selectedItem.includes(item.id)}
            disabled={item.order_ack_by_name}
            onChange={(e: any, v: any) => {
              if (v.checked) {
                setSelectedItem([...selectedItem, item.id]);
              } else {
                let newData = selectedItem.filter((key: any) => key !== item.id);
                setSelectedItem(newData);
              }
            }}
          />,
          { padding: "10px" }
        ),
        ...patientRecordColumns.reduce((data: any, header) => {
          const key = header.accessor;

          if (key === "created_at") {
            data[key] = columnTableCenter(dateToStringBE(item[key]));
            // } else if (key === "value") {
            //   data[key] = columnTableCenter(formatComma(item[key]));
          } else if (key === "created_by") {
            data[key] = columnTableCenter(
              (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
            );
          } else if (key === "birthdate") {
            data[key] = columnTableCenter(formatBirthdate(item[key]));
          } else if (["valid", "sent"].includes(key)) {
            data[key] = columnTableIconCheck(!!item[key]);
          } else if (
            [
              "file_name",
              "full_name",
              "father",
              "mother",
              "career",
              "home_address",
              "district",
              "province",
            ].includes(key)
          ) {
            data[key] = columnTable(item[key]);
          } else {
            data[key] = columnTableCenter(item[key]);
          }

          return data;
        }, {}),
      };
    });
  }, [selectedItem, props.PatientRecordInterfaceSequence?.patientRecordList, patientRecordColumns]);

  return (
    <div>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_PATIENT_RECORD_INTERFACE}`, null);
            props.setProp(`successMessage.${CARD_PATIENT_RECORD_INTERFACE}`, null);
          }}
          error={props.errorMessage?.[CARD_PATIENT_RECORD_INTERFACE]}
          success={props.successMessage?.[CARD_PATIENT_RECORD_INTERFACE]}
          languageUX={props.languageUX}
        />
      )}

      <CardPatientRecordInterfaceUX
        paginatedItems={props.PatientRecordInterfaceSequence?.paginatedItems}
        searchPatientRecord={props.PatientRecordInterfaceSequence?.searchPatientRecord}
        onClickClear={handleClearFilter}
        onChangeData={handleChangeData}
        onChangeDate={handleChangeDate}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        patientRecordData={patientRecordData}
        patientRecordColumns={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedItem.length ===
                    (props.PatientRecordInterfaceSequence?.patientRecordList || []).length &&
                  (props.PatientRecordInterfaceSequence?.patientRecordList || []).length > 0
                }
                onChange={handleSelectAll}
              />
            ),
            accessor: "check",
            width: 60,
          },
          ...patientRecordColumns,
        ]}
        buttonSeach={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearchData}
            // data
            paramKey={`${CARD_PATIENT_RECORD_INTERFACE}_search`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_PATIENT_RECORD_INTERFACE}_search`]}
            // config
            color="blue"
            size="medium"
            fluid={true}
            title="Search"
          />
        }
        buttonAdd={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={() => handleSaveData("add")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_PATIENT_RECORD_INTERFACE}_add`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_PATIENT_RECORD_INTERFACE}_add`]}
            // config
            color="blue"
            size="medium"
            fluid={true}
            title="Add Record"
          />
        }
        buttonEdit={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={() => handleSaveData("edit")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_PATIENT_RECORD_INTERFACE}_edit`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_PATIENT_RECORD_INTERFACE}_edit`]}
            // config
            color="green"
            size="medium"
            fluid={true}
            title="Edit Date"
          />
        }
      />
    </div>
  );
};

CardPatientRecordInterface.displayName = "CardPatientRecordInterface";

export default React.memo(CardPatientRecordInterface);

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  error_message: 150,
  created_by: 120,
  created_at: 150,
  sent: 70,
  file_name: 200,
  version: 80,
  company_code: 160,
  debtor_group: 160,
  hn: 120,
  full_name: 180,
  father: 180,
  mother: 180,
  birthdate: 150,
  home_address: 200,
  district: 150,
  province: 150,
  citizen_no: 200,
};

const patientRecordColumns = GenerateTableHeader(columnWidths);
